import { Alert, Badge, Code, Group, Paper, Stack, Table, Text, Title } from '@mantine/core'
import { useEvents } from '../data/catalog'
import { useScrapeRuns } from '../data/scrapeRuns'
import type { ScrapeRun } from '../data/scrapeRuns'
import EventTable from '../components/EventTable'

/**
 * TSL management: the scrape and its history.
 *
 * The opposite of RIS. TSL publishes a server-rendered diary with real event
 * IDs, and its live stream supplies the series, track, planned start and
 * session type directly. So events are discovered rather than typed, and what
 * needs managing is the scrape rather than the data.
 */
export default function VendorTsl() {
  const { events, error } = useEvents('tsl')
  const runs = useScrapeRuns('tsl')

  return (
    <Stack gap="xl">
      <div>
        <Title order={1} size="h2">
          TSL Timing
        </Title>
        <Text c="dimmed" size="sm">
          Events are discovered from the published diary rather than entered by hand.
        </Text>
      </div>

      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Scrape history
          </Title>
          <Text c="dimmed" size="sm">
            A scraper's failure mode is silence: TSL publishes no contract, and a parser that
            returns nothing looks exactly like a quiet month. So each run records what it found,
            and the parser cross-checks against the page's own event count and fails loudly if they
            disagree.
          </Text>
        </div>

        {runs?.length === 0 && (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center" size="sm">
              No runs recorded yet. The scrape runs nightly at 03:00 London, or on demand with{' '}
              <Code>gcloud run jobs execute tsl-diary --region=europe-west4</Code>.
            </Text>
          </Paper>
        )}

        {runs && runs.length > 0 && (
          <Paper withBorder>
            <Table.ScrollContainer minWidth={720}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={180}>Run</Table.Th>
                    <Table.Th w={90}>Status</Table.Th>
                    <Table.Th w={70}>Took</Table.Th>
                    <Table.Th>Found</Table.Th>
                    <Table.Th>Months</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {runs.map((run) => (
                    <RunRow key={run.id} run={run} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}
      </Stack>

      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Catalogued events
          </Title>
          <Text c="dimmed" size="sm">
            Circuit, dates and name only. Session timetables are a separate and more expensive
            scrape, worth fetching once an organisation has attached to an event rather than for
            everything TSL runs.
          </Text>
        </div>
        {error && <Alert color="red">{error}</Alert>}
        {events && events.length > 0 && (
          <>
            <Paper withBorder>
              <EventTable events={events} limit={40} />
            </Paper>
            {events.length > 40 && (
              <Text c="dimmed" size="xs">
                Showing 40 of {events.length}.
              </Text>
            )}
          </>
        )}
        {events?.length === 0 && (
          <Text c="dimmed" size="sm">
            Nothing catalogued yet.
          </Text>
        )}
      </Stack>
    </Stack>
  )
}

function RunRow({ run }: { run: ScrapeRun }) {
  const started = run.startedAt?.toDate?.()
  const finished = run.finishedAt?.toDate?.()
  const took =
    finished && started ? Math.round((finished.getTime() - started.getTime()) / 1000) : null

  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{started ? started.toLocaleString('en-GB') : 'Unknown time'}</Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color={run.status === 'ok' ? 'green' : 'red'} size="sm">
          {run.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" ff="monospace">
          {took !== null ? `${took}s` : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {run.found} found · {run.created} new · {run.updated} updated · {run.unchanged} unchanged
        </Text>
        {run.errors && run.errors.length > 0 && (
          <Stack gap={2} mt={4}>
            {run.errors.map((e, i) => (
              <Text key={i} size="xs" c="red">
                {e}
              </Text>
            ))}
          </Stack>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {run.months?.map((m) => (
            <Text key={`${m.year}-${m.month}`} size="xs" c="dimmed" ff="monospace">
              {m.year}-{String(m.month).padStart(2, '0')}: {m.failed ? 'failed' : m.found}
            </Text>
          ))}
        </Group>
      </Table.Td>
    </Table.Tr>
  )
}
